import {
  RESET_VIDEO_NEXT,
  VIDEO_ALL_EPG,
  VIDEO_CURRENT,
  VIDEO_NEXT,
  VIDEO_OPTIONS,
  VIDEO_PLAYER_EPGS,
  VIDEO_PLAYER_TOGGLE,
  VIDEO_RESET, VIDEO_SET_PLAYER_STATE
} from 'common/constants/action-types';
import {
  getLiveTimestamp,
  getPlaylist,
  getToday,
  hasTokenInMemory,
  sortBySeasonAndEpisode
} from 'common/services/helpers';
import request from 'common/services/request';
import { createContinueWatchingPropagator } from 'common/utils/last-watched';
import { isEmpty, isNil, uniqueId } from 'lodash';
// import { unlockChannelsTemporarily } from './profile-settings';

/**
 * Set video player current url to play
 * @param epg {object} - { isOpen: bool, isPlaying: bool, channelId: string, video: { start: number, end: number } }
 * @returns {function(*)}
 */
export const setCurrentVideo = (epg) => {
  return (dispatch, getState) => {
    try {
      const isVod = epg.video.vod;
      const video = getPlaylist(epg.channelId, epg.video.start, epg.video.stop, isVod);
      const { videoPlayer, liveChannels } = getState();
      const nextPosition = epg.video?.channelPosition;
      const currentPosition = videoPlayer.video?.channelPosition;

      const previousIsPlaying = videoPlayer.isPlaying;

      const nextSource = video.source;
      const currentSource = videoPlayer.video.source;

      // When video source, channel id or channel position are different
      const playingNewChannel = nextSource !== currentSource || (!isEmpty(currentPosition) && nextPosition != currentPosition);
      const isPlaying = previousIsPlaying ? !playingNewChannel : false;

      const access = isVod ? epg.video.access : (liveChannels[epg.channelId]?.access || epg?.video?.access);
      const playingSameChannel = nextPosition === currentPosition;
      // Force unlock channel if playing the same channel
      const locked = playingSameChannel ? false : liveChannels[epg.channelId]?.locked;

      // Preload current time on loading of channel
      const startPosition = isNil(epg.video.startPosition) ? -1 : epg.video.startPosition;
      const currentTime = Math.max(video.now && startPosition === -1
        ? getLiveTimestamp() - video.start
        : epg.video.startPosition, 0);

      dispatch({
        type: VIDEO_CURRENT,
        payload: {
          ...epg,
          isPlaying,
          allEpgs: [epg.currentEpg],
          epgs: {
            current: epg.currentEpg
          },
          video: {
            ...epg.video,
            ...video,
            access,
            locked,
            currentTime,
            playlistStart: epg.video.start,
          }
        }
      });
    } catch (error) {
      console.warn(error);
    }
  };
};

// live playlists continue past the end of a show, switch shows without reloading
export const continueLiveWithNextEpg = (epg) => {
  return (dispatch, getState) => {
    dispatch({
      type: VIDEO_CURRENT,
      payload: {
        channelId: epg.chanId || epg.chan_id,
        currentEpg: epg,
        epgs: { current: epg },
        video: {
          start: epg.start,
          stop: epg.stop,
          duration: epg.stop - epg.start,
          startPosition: 0,
          // progress() already marked the previous show as not live
          isLiveEpg: getState().videoPlayer.video?.now
        }
      }
    });
  };
};

export const setNextVideo = (epg, position, startPosition = -1, autoPlay = false) => {
  return (dispatch) => {
    dispatch({
      type: VIDEO_NEXT,
      payload: {
        channelId: epg.chan_id,
        start: epg.start,
        stop: epg.stop,
        startPosition,
        autoPlay,
        position,
        epg,
      }
    });
  };
};

export const resetNextVideo = () => {
  return (dispatch) => {
    dispatch({
      type: RESET_VIDEO_NEXT,
      payload: {}
    });
  };
};

/**
 * Set video options - volume, current time, live, play/pause, mute
 * @param {Partial<import('common/reducers/videoPlayer').IVideo>} options
 * @returns {Function}
 */
export const setVideoOptions = (options) => {
  return (dispatch) => {
    dispatch({
      type: VIDEO_OPTIONS,
      payload: options
    });
  };
};

/**
 * Set video player container current state
 * @param state {object} - { isOpen: bool, isPlaying: bool }
 * @returns {function(*)}
 */
export const toggleVideoPlayerState = (state) => {
  return (dispatch) => {
    dispatch({
      type: VIDEO_PLAYER_TOGGLE,
      payload: state
    });
  };
};

/**
 * Set video player epgs
 * @param channelId {string}
 * @param start {number}
 * @returns {Function}
 */
export const setVideoPlayerEpgs = (channelId, start) => {
  return (dispatch, getState) => {
    try {
      const { videoPlayer, lastWatched } = getState();

      const vod = videoPlayer?.video?.vod;
      const allEpgs = videoPlayer?.allEpgs?.map(createContinueWatchingPropagator(lastWatched)) || [];

      const currentEpgIndex = vod
        ? // Because VOD does not play automatically, we fallback to first epg in the array
        (channelId && allEpgs.findIndex((e) => e.id === channelId)) || 0
        : // Find current epg by start time and channel id
        allEpgs.findIndex(
          (item) => item.start === start && item.chan_id === channelId
        );

      // the show, or the one before it, may be in a day not loaded yet
      if (
        !vod &&
        allEpgs.length &&
        (currentEpgIndex === 0 || (currentEpgIndex === -1 && start < allEpgs[0].start))
      ) {
        dispatch(loadOlderEpgsForChannel(channelId, start));
      }

      if (currentEpgIndex !== null && currentEpgIndex !== -1) {
        const nextEpgIndex = currentEpgIndex + 1;
        const pastEpgIndex = currentEpgIndex - 1;

        const epgs = {
          all: allEpgs,
          current: allEpgs[currentEpgIndex],
          next: typeof allEpgs[nextEpgIndex] !== 'undefined' ? allEpgs[nextEpgIndex] : null,
          prev: typeof allEpgs[pastEpgIndex] !== 'undefined' ? allEpgs[pastEpgIndex] : null,
          initialOffset: currentEpgIndex + 1,
          initialToIndex: currentEpgIndex
        };
        dispatch({
          type: VIDEO_PLAYER_EPGS,
          payload: epgs
        });
      }
    } catch (error) {
      console.warn('Could not set epgs', error);
    }
  };
};

// EPG days are UTC day offsets from today (ott-epg getDayTS)
const EPG_DAY = 86400;
const INITIAL_EPG_DAYS = [0, -1];
const MAX_EPG_DAYS_BACK = -45;
const epgDayOffset = (timestamp) =>
  Math.floor(timestamp / EPG_DAY) - Math.floor(getToday().getTime() / 1000 / EPG_DAY);
const epgsLoading = {};

const fetchEpgs = async (channelId, query) => {
  const { data } = await request({ method: 'get', url: `/epgs/${channelId}?${query}` });
  return data || [];
};

const mergeEpgs = (...lists) => {
  const byStart = {};
  const merged = [];
  lists.forEach((list) =>
    (list || []).forEach((epg) => {
      const key = epg.chan_id + ':' + epg.start;
      if (!byStart[key]) {
        byStart[key] = true;
        merged.push(epg);
      }
    })
  );
  return merged.sort((a, b) => a.start - b.start);
};

/**
 * Set video player container epgs for current channel: upcoming, today and yesterday
 * @param channelId {string}
 * @returns {function(*)}
 */
export const getAllEpgsForChannel = (channelId) => {
  return async (dispatch, getState) => {
    try {
      const lists = await Promise.all([
        fetchEpgs(channelId, 'future'),
        ...INITIAL_EPG_DAYS.map((day) => fetchEpgs(channelId, `offset=${day}`))
      ]);
      // a slow response for the previous channel can arrive after a zap
      const { videoPlayer, lastWatched } = getState();
      if (videoPlayer.channelId !== channelId) {
        return;
      }
      const fresh = mergeEpgs(...lists).map(createContinueWatchingPropagator(lastWatched));
      // a refresh keeps the older days loaded since
      const refresh = videoPlayer.allEpgsChannelId === channelId && fresh.length > 0;
      const older = refresh
        ? (videoPlayer.allEpgs || []).filter((epg) => epg.start < fresh[0].start)
        : [];
      dispatch({
        type: VIDEO_ALL_EPG,
        payload: {
          allEpgs: older.length ? mergeEpgs(older, fresh) : fresh,
          allEpgsChannelId: channelId,
          allEpgsOldestDay: refresh
            ? Math.min(videoPlayer.allEpgsOldestDay ?? 0, ...INITIAL_EPG_DAYS)
            : Math.min(...INITIAL_EPG_DAYS),
          allEpgsComplete: refresh ? !!videoPlayer.allEpgsComplete : false
        }
      });
    } catch (error) {
      console.warn(error);
    }
  };
};

/**
 * Load older epg days for current channel, back to untilTimestamp or one day
 * @param channelId {string}
 * @param {number} [untilTimestamp]
 * @returns {function(*)}
 */
export const loadOlderEpgsForChannel = (channelId, untilTimestamp) => {
  return async (dispatch, getState) => {
    const { videoPlayer } = getState();
    if (
      epgsLoading[channelId] ||
      videoPlayer.allEpgsChannelId !== channelId ||
      videoPlayer.allEpgsComplete ||
      isNil(videoPlayer.allEpgsOldestDay)
    ) {
      return;
    }
    epgsLoading[channelId] = true;
    try {
      let day = videoPlayer.allEpgsOldestDay;
      const target = Math.max(
        MAX_EPG_DAYS_BACK,
        isNil(untilTimestamp) ? day - 1 : Math.min(day - 1, epgDayOffset(untilTimestamp) - 1)
      );
      const days = [];
      for (let d = day - 1; d >= target; d--) {
        days.push(d);
      }
      const lists = await Promise.all(days.map((d) => fetchEpgs(channelId, `offset=${d}`)));
      // archives have no gaps: the first empty day is where it ends
      const loaded = [];
      let ended = false;
      for (let i = 0; i < lists.length && !ended; i++) {
        day = days[i];
        ended = !lists[i].length;
        loaded.push(lists[i]);
      }
      const state = getState();
      if (state.videoPlayer.channelId !== channelId || state.videoPlayer.allEpgsChannelId !== channelId) {
        return;
      }
      const added = [].concat(...loaded);
      dispatch({
        type: VIDEO_ALL_EPG,
        payload: {
          // same array when nothing loaded, or setVideoPlayerEpgs asks again
          allEpgs: added.length
            ? mergeEpgs(
                state.videoPlayer.allEpgs,
                added.map(createContinueWatchingPropagator(state.lastWatched))
              )
            : state.videoPlayer.allEpgs,
          allEpgsOldestDay: day,
          allEpgsComplete: ended
        }
      });
    } catch (error) {
      console.warn(error);
    } finally {
      epgsLoading[channelId] = false;
    }
  };
};

/**
 * Sets all epgs RAW function
 * @param {import('common/constants/data-types').Epg[]} epgs 
 * @returns 
 */
export const setAllEgps = (epgs) => {
  return (dispatch) => {
    if (!epgs) {
      console.warn('Epgs is undefined or null');
      return;
    }
    dispatch({
      type: VIDEO_ALL_EPG,
      payload: {
        allEpgs: epgs
      }
    });
  };
}


/**
 * Sets all epgs sorted by season and episode
 * Mainly used for VOD implementation
 * @param {import('common/constants/data-types').Epg[]} epgs 
 * @returns 
 */
export const setAllEgpsSorted = (epgs = []) => {
  return (dispatch) => {
    dispatch({
      type: VIDEO_ALL_EPG,
      payload: {
        allEpgs: sortBySeasonAndEpisode(epgs)
      }
    });
  };
}



/**
 * @param {string} baseContentId
 * @returns {Promise<import('common/constants/data-types').Epg[] | undefined>}
 */
export const getAllEpgsForSeries = (baseContentId) => {
  return async (dispatch, getState) => {
    try {
      const { lastWatched } = getState();
      let ajax = request({
        method: 'get',
        url: `/epgs/byField/baseContentId?term=${baseContentId}`
      });
      const { data } = await ajax;
      const propagateContinueWatching = createContinueWatchingPropagator(lastWatched);
      const allEpgs = data?.map(epg => ({ ...propagateContinueWatching(epg) })) || [];
      const reversedData = [...allEpgs].reverse();
      return reversedData;
    } catch (error) {
      console.warn(error)
    }
  };
};

/**
 *
 * @param {undefined | number} currentTime
 * @returns
 */
export const resetVideo = () => {
  return (dispatch, getState) => {
    const { videoPlayer } = getState();
    // Check if its in reset state
    if (videoPlayer.isOpen) {
      dispatch({
        type: VIDEO_RESET,
        payload: {}
      });
    }
  };
};

export const playVideoVod = (currentepg, options) => {
  return (dispatch) => {
    dispatch(playVideo({ ...currentepg, chan_id: currentepg.id }, { ...options, vod: true, vodAsset: currentepg, }));
  }
}

export const playVideo = (currentepg, options) => {
  const defaultOptions = { startPosition: -1, channelPosition: null, vod: false };
  options = { ...defaultOptions, ...options };
  let { startPosition, hash, channelPosition, vod, isFullscreen } = options;
  return (dispatch, getState) => {
    // If channelPosition is -1 use the previous set channel position
    if (channelPosition === -1) {
      channelPosition = getState().videoPlayer?.video?.channelPosition;
    }
    return dispatch(
      setCurrentVideo({
        currentEpg: currentepg,
        isOpen: true,
        channelId: currentepg.chan_id,
        isStopped: false,
        video: {
          start: currentepg.start,
          stop: currentepg.stop,
          duration: currentepg.stop - currentepg.start,
          startPosition,
          hash,
          channelPosition,
          vod,
          access: currentepg.access,
          isFullscreen,
        }
      })
    );
  };
};

export const playVideoByPosition = (position, options) => {
  return (dispatch, getState) => {
    try {
      const { liveChannels, favoriteChannels } = getState();
      const channels = [...Object.values(liveChannels), ...Object.values(favoriteChannels)];
      const channel = channels.find(e => e.position === parseInt(position));
      if (channel) {
        return dispatch(playVideo(channel.currentepg, { channelPosition: channel.position, ...options }))
      } else {
        console.warn('Could not find channel with position', position);
      }
    } catch (error) {
      console.warn(error)
    }
  }
}

export const playVideoByEventId = (eventid, options) => {
  return async (dispatch) => {
    try {
      const { data } = await request({
        method: 'get',
        url: `/epgs/byField/eventid?term=${eventid}`,
      });
      if (!isEmpty(data) && !isEmpty(data[0])) {
        return dispatch(playVideo(data[0]));
      } else {
        return -1;
      }
    } catch (error) {
      console.warn(error)
    }
  }
}

/**
 * Sets vod asset
 * with sorted epgs
 * Mainly used for VOD implementation
 * @param {import('common/constants/data-types').Epg} asset 
 * @param {boolean} isFullscreen
 * @returns 
 */

export const setVodAsset = (asset, isFullscreen = true) => {
  return (dispatch) => {
    dispatch(resetVideo());
    dispatch(toggleVideoPlayerState({ isOpen: true }))
    dispatch({
      type: VIDEO_OPTIONS,
      payload: {
        vodAsset: asset,
        vod: true,
        isFullscreen,
        access: asset.access,
      }
    });
    dispatch(setAllEgpsSorted(asset?.epgs));
  };
}


export const stopVideo = () => {
  return (dispatch) => {
    dispatch(toggleVideoPlayerState({ isStopped: true }));
  };
};

export const unStopVideo = () => {
  return (dispatch) => {
    dispatch(toggleVideoPlayerState({ isStopped: false }));
  };
};

export const setPlayerPlaying = (state) => {
  return toggleVideoPlayerState({
    isPlaying: state
  });
};

export const setPlayerControlsState = (state) => {
  return (dispatch) => {
    dispatch({
      type: VIDEO_SET_PLAYER_STATE,
      payload: state
    });
  };
};

export const resetEpgs = () => {
  return (dispatch) => {
    dispatch({
      type: VIDEO_ALL_EPG,
      payload: { epgs: null, allEpgs: null }
    });
  };
};

/**
 * 
 * @param {import('common/constants/data-types').EpgRecord} payload 
 */
export const sendEpgRecord = async (payload) => {
  try {
    if (!hasTokenInMemory()) {
      console.warn('No token in memory, cannot send epg record');
      return false;
    }
    // if we send both request will fail
    // eventId is preferred, but not present in vod epgs
    if (!isEmpty(payload.eventId)) {
      delete payload["contentId"];
    }
    await request({
      method: 'POST',
      url: "/epgs/event/record",
      data: payload,
    });
    // NOT recommended to dispatch action
    // 
    // Uncomment if you want to store the record in redux
    // After sending the record to the server
    // dispatch({
    //   type: VIDEO_SEND_PLAYER_RECORD,
    //   payload
    // });
    return true;
  } catch (error) {
    console.warn("Failed to send epg record", error);
    return false;
  }
};
/**
 * 
 * @param {number | undefined} hash 
 * @returns 
 */
export const setVideoHash = (hash) => {
  return (dispatch) => {
    dispatch(setVideoOptions({
      hash: hash || uniqueId('video')
    }))
  }
}
